import { ArrowRight } from "lucide-react";
import { Sun, Cloud, Heart, Star, Rainbow, Flower, Smiley, Squiggle, SpiralCircle, Balloon, ChildWithBalloons, ChildWithBalloons2 } from "./Illustrations";

const LOGO_URL =
  "/images/logo.png";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-6 md:pt-10 pb-20 md:pb-28 overflow-hidden"
    >
      {/* Sky decorations */}
      <Sun className="absolute top-8 right-8 md:right-20 animate-spin-slow opacity-90" size={90} />
      <Rainbow className="absolute top-12 right-28 md:right-44 opacity-90 hidden sm:block" size={150} />
      <Cloud className="absolute top-20 left-8 md:left-32 animate-float opacity-95" size={100} color="#FFFFFF" />
      <Cloud className="absolute top-32 right-1/3 animate-float opacity-90" size={70} color="#FFFFFF" />
      <Heart className="absolute top-1/3 left-10 animate-pulse-soft hidden md:block" size={36} color="#FBC4D0" />
      <Star className="absolute top-44 right-12 animate-pulse-soft hidden md:block" size={28} color="#FBE4A8" />
      <Star className="absolute bottom-32 left-20 animate-pulse-soft hidden lg:block" size={24} color="#B8D6E8" />
      <SpiralCircle className="absolute bottom-20 right-10 hidden lg:block animate-spin-slow" size={70} color="#B8D6E8" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-24">
        {/* Wide logo banner with hand-drawn frame */}
        <div className="relative" data-testid="hero-logo-banner">
          <Balloon className="absolute -top-12 -left-4 md:-left-12 animate-float hidden md:block" size={70} color="#FBC4D0" />
          <Balloon className="absolute -top-16 -right-4 md:-right-12 animate-float hidden md:block" size={70} color="#B8D6E8" />
          <Flower className="absolute -bottom-8 -left-6 hidden md:block animate-wiggle" size={70} color="#FBC4D0" />
          <Flower className="absolute -bottom-6 -right-6 hidden md:block animate-wiggle" size={60} color="#FBE4A8" />

          <div className="relative bg-white rounded-[2.5rem] overflow-hidden border-4 border-dashed border-brand-pink shadow-[0_20px_0_-8px_#FBE4A8,0_40px_0_-16px_#B8D6E8]">
            <div className="absolute inset-0 bg-gradient-to-br from-brand-yellowSoft via-white to-brand-blueSoft" />
            <img
              src={LOGO_URL}
              alt="Ορίζοντες Ανάπτυξης — Κέντρο Ειδικών Θεραπειών"
              className="relative w-full h-[280px] sm:h-[360px] md:h-[460px] lg:h-[520px] object-contain p-2"
              data-testid="hero-image"
            />
          </div>
        </div>

        {/* Content below banner */}
        <div className="relative mt-20 md:mt-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-yellow border-2 border-brand-text/15 mb-6 -rotate-2">
              <Smiley size={22} />
              <span className="font-body text-sm font-bold text-brand-text">
                Ορίζοντες Ανάπτυξης — Περιστέρι
              </span>
            </div>

            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-brand-text leading-[1.1] tracking-tight">
              Ενισχύουμε την{" "}
              <span className="relative inline-block crayon-underline">
                Ανάπτυξη
              </span>
              ,<br />
              Αγκαλιάζουμε το{" "}
              <span className="font-hand text-brand-primary text-5xl sm:text-6xl lg:text-7xl">
                Παιδί
              </span>
              <Heart size={36} className="inline-block ml-2 animate-pulse-soft align-middle" color="#F4A899" />
            </h1>

            <Squiggle className="my-6" width={180} color="#B8D6BE" />

            <p className="font-body text-lg text-brand-textSoft leading-relaxed max-w-2xl">
              Στους <strong className="text-brand-text">Ορίζοντες Ανάπτυξης</strong>, εξειδικευμένοι
              θεραπευτές συνεργάζονται με τα παιδιά και τις οικογένειές τους,
              προσφέροντας ολιστική φροντίδα σε ένα ζεστό, ασφαλές περιβάλλον στο
              Περιστέρι — δίπλα στο μετρό Αγίου Αντωνίου, με εύκολη πρόσβαση από
              όλα τα Δυτικά Προάστια της Αθήνας.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <button
                data-testid="hero-services-cta"
                onClick={() => scrollTo("ypiresies")}
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-brand-primary text-white font-bold hover:bg-brand-primaryHover transition-all shadow-[0_5px_0_#5B4F46] hover:translate-y-[3px] hover:shadow-[0_2px_0_#5B4F46]"
              >
                Οι Υπηρεσίες μας
                <ArrowRight size={18} />
              </button>
              <button
                data-testid="hero-contact-cta"
                onClick={() => scrollTo("epikoinonia")}
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-brand-text font-bold border-2 border-brand-text/20 hover:border-brand-primary hover:text-brand-primary transition-all shadow-[0_5px_0_#5B4F46]/15 hover:translate-y-[3px]"
              >
                Επικοινωνία
              </button>
            </div>

            {/* Hand-drawn kids with balloons */}
            <div className="mt-12 flex items-end gap-2 sm:gap-6 -ml-2">
              <ChildWithBalloons className="animate-float" size={130} />
              <ChildWithBalloons2 className="animate-float" size={130} />
            </div>
          </div>

          <div className="lg:col-span-5">
            <div
              data-testid="hero-assessment-card"
              className="relative bg-white rounded-[2rem] border-4 border-dashed border-brand-secondary p-7 shadow-[0_15px_0_-5px_#FBC4D0]"
            >
              <Star size={40} className="absolute -top-5 -right-3 animate-wiggle" color="#FBE4A8" />
              <Heart size={32} className="absolute -bottom-4 -left-3 animate-pulse-soft" color="#FBC4D0" />

              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 rounded-full bg-brand-pink flex items-center justify-center border-2 border-brand-text/15">
                    <Heart size={26} color="#F4A899" />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-brand-text text-lg">Δωρεάν Αξιολόγηση</div>
                    <div className="font-hand text-brand-primary text-base leading-none">για κάθε νέο παιδί</div>
                  </div>
                </div>
                <p className="font-body text-sm text-brand-textSoft leading-relaxed">
                  Η πρώτη συνεδρία γνωριμίας και αξιολόγησης προσφέρεται εντελώς
                  δωρεάν. Πάμε μαζί στο πρώτο βήμα! ✨
                </p>
                <button
                  onClick={() => scrollTo("free-assessment")}
                  data-testid="hero-free-assessment-link"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-brand-primary hover:text-brand-primaryHover transition-colors"
                >
                  Μάθετε περισσότερα
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>

            <Rainbow className="mx-auto mt-8 hidden lg:block" size={220} />
          </div>
        </div>
      </div>
    </section>
  );
}
